/**
 * The message half of the wire contract.
 *
 * State replication is described by `./state.ts`; everything that travels as a
 * discrete message is named and typed here, once, for both sides. A client and
 * a server that disagree on these names fail silently at runtime, so the names
 * are frozen constants rather than inline string literals.
 *
 * ## Client messages are intents, not facts
 *
 * `ARCHITECTURE.md`: "Client sends an **intent** — `{moveTo}`, `{useSkill}`,
 * `{pickUp}`. Never a position, never a damage number." Everything here is a
 * request the server is free to clamp or refuse, and nothing here is trusted.
 * That is why `moveTo` carries a target and not a route: a route is an answer,
 * and answers are the server's.
 *
 * ## Adding the next intent
 *
 * Three lines, in three places, and the compiler finds the ones you forgot:
 *
 *   1. a name in `CLIENT_MESSAGE`;
 *   2. its payload interface, targeting a cell as `Vec2` if it happens
 *      somewhere — `useSkill` is `{ skill, target }`, `pickUp` is `{ item }`;
 *   3. an entry in the `ClientMessages` table below.
 *
 * Miss step 3 and `ClientMessagePayload` stops compiling, because it indexes
 * that table by the whole name union. The exhaustiveness is not a convention
 * anybody has to remember.
 *
 * ## Two of the five are parsed here, and three are not
 *
 * `moveTo`, `attack` and `pickUp` are taken apart in
 * `apps/game/src/rooms/intents.ts`, which is also where they are judged against
 * the world. `useSkill` and `spendSkillPoint` have their **shape** parsed in
 * this file, and that split is deliberate rather than tidy:
 *
 *   - What a message *is* — its keys, their types, and the rule that an unknown
 *     key fails the whole payload — is the wire contract, which is this
 *     package. What a message *means* against a floor is a rule, which is
 *     `@d2/core`'s `admitCast` and `spendSkillPoint`. The room composes the two
 *     and adds only what it alone knows: the flood allowance, and which
 *     connection is speaking.
 *   - The reserved sequence lives here (`NO_INTENT_APPLIED`,
 *     `FIRST_INTENT_SEQUENCE`), so the predicate that enforces it belongs here
 *     too. `isIntentSequence` is exported for exactly that reason: there should
 *     be one definition of a sequence a client may send, and today there are
 *     two.
 *
 * The honest reading of the split is that the three older parsers should follow
 * these two rather than the other way round — they need nothing from the room
 * either, and `intents.ts`'s `admitMoveTo` would keep the ordering of its checks
 * and lose only its private copy of `hasExactly`. That is a change to another
 * package and is not made here; it is `TODO.md` #440's argument arriving at the
 * message layer, and it is in the handover.
 *
 * ## Failing closed is the whole of what a parser here does
 *
 * `TODO.md` #441 is what the alternative looks like: the create half of a room's
 * options reads three keys and ignores everything else, so a client built
 * against a newer server can send an option this one does not read and be given
 * a floor that quietly ignores it. Every parser in this file refuses a payload
 * carrying a key it did not ask for, which is what makes "cast that on somebody
 * else's behalf" and "spend twenty points in one click" unexpressible rather
 * than merely refused.
 */
/** Client -> server. */
export const CLIENT_MESSAGE = Object.freeze({
    ping: 'ping',
    moveTo: 'moveTo',
    attack: 'attack',
    pickUp: 'pickUp',
    /** `ARCHITECTURE.md`'s own name for the third intent: *"`{moveTo}`, `{useSkill}`, `{pickUp}`"*. */
    useSkill: 'useSkill',
    spendSkillPoint: 'spendSkillPoint',
    /** "I am taking the stairs." The way down, and the whole of it — see `DescendPayload`. */
    descend: 'descend',
});
/** Server -> client. */
export const SERVER_MESSAGE = Object.freeze({
    pong: 'pong',
    /** The seat's own point ledger. Addressed to one connection, never broadcast — see `ProgressionPayload`. */
    progression: 'progression',
    /** What one player's death cost them. Addressed to the seat that died, never broadcast — see `RunTollPayload`. */
    runToll: 'runToll',
    /** The code this run is invited by. Sent to each seat as it sits down — see `InvitePayload`. */
    invite: 'invite',
});
/**
 * The value `PlayerState.lastSequence` carries before the server has applied
 * any intent at all — and therefore a sequence number no client may send.
 *
 * A schema field starts at zero, so zero is the one value that cannot also mean
 * "intent zero was accepted". Reserving it is what lets a fresh player and an
 * acknowledged first click be told apart without a second field.
 */
export const NO_INTENT_APPLIED = 0;
/** The first sequence number a client may send. Counting starts above the reserved zero. */
export const FIRST_INTENT_SEQUENCE = 1;
// ---------------------------------------------------------------------------
// Taking a payload apart.
//
// A Colyseus message arrives as whatever msgpack decoded; the declared payload
// type above is a promise the client made and is free to break. So everything
// below takes `unknown` and hands back either a value a room may act on or
// `null`, which the room reports as its own `malformed`. The refusal is a bare
// null rather than a reason because there is exactly one reason: this is not the
// shape of that message. Which *rule* refuses it afterwards is core's, and core
// has a vocabulary for it.
// ---------------------------------------------------------------------------
/**
 * The longest id this wire will carry, for a skill or for a body.
 *
 * Sixty-four, and it is the same sixty-four in three places that were each
 * derived separately: `apps/game`'s `MAX_ENTITY_ID_LENGTH` bounds the id it
 * compares against every body and every drop on the floor, and `apps/api`'s
 * `SKILL_ID_MAX_LENGTH` is the width of `skill_allocations.skill_id` — so a
 * skill id longer than this is one no spend could ever be stored under, whatever
 * core thinks of it.
 *
 * It is a bound rather than a format check, because the format is not this
 * package's to know (`TODO.md` #264 has not decided what an id *is*). What is
 * decided is that *unbounded* is not something a client gets to choose: an id is
 * compared against a table and against a floor, and a megabyte of string is a
 * megabyte of comparison per message.
 */
export const MAX_WIRE_ID_LENGTH = 64;
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/**
 * `true` when the object carries these keys and nothing else at all.
 *
 * The whole of failing closed. `Object.keys` is the comparison rather than a
 * loop of `in`, so a key nobody asked for is *counted* — which is what makes an
 * extra field a refusal instead of something quietly dropped.
 */
const hasExactly = (record, keys) => {
    const present = Object.keys(record);
    return present.length === keys.length && keys.every((key) => present.includes(key));
};
/**
 * A sequence number a client may actually have counted to.
 *
 * A safe integer, because a sequence is a counter and anything else is a client
 * that is not counting; at or above `FIRST_INTENT_SEQUENCE`, so the reserved
 * zero cannot be sent. Exported because it is the enforcement of a rule this
 * file declares, and there should be one copy of it rather than one per
 * validator (`apps/game/src/rooms/intents.ts` holds the other today).
 */
export const isIntentSequence = (value) => typeof value === 'number' && Number.isSafeInteger(value) && value >= FIRST_INTENT_SEQUENCE;
/** An id a server could look something up by: a non-empty string, bounded. */
const isWireId = (value) => typeof value === 'string' && value.length > 0 && value.length <= MAX_WIRE_ID_LENGTH;
/**
 * The cast a message describes, or `null`.
 *
 * Shape only, and the three refusals worth naming are the ones a rule would
 * never catch: a key nobody asked for (so a cast cannot name its caster), an
 * absent `target` (so a forgotten field is not a self-cast), and an unbounded
 * id. A skill id this server has never heard of is **accepted here** and refused
 * by `admitCast` — the same line `admitAttack` draws around a body that does not
 * exist.
 */
export const parseUseSkill = (message) => {
    if (!isRecord(message) || !hasExactly(message, ['skill', 'target', 'sequence']))
        return null;
    const { skill, target, sequence } = message;
    if (!isWireId(skill))
        return null;
    if (target !== null && !isWireId(target))
        return null;
    if (!isIntentSequence(sequence))
        return null;
    return { skill, target, sequence };
};
/**
 * The skill-point spend a message describes, or `null`.
 *
 * There is deliberately **no count of points in the payload**, so the parser
 * refuses one: core's `spendSkillPoint` takes an amount and defaults it to one,
 * and a wire that carried the amount would let a single message empty a
 * character's ledger into one skill while the sequence still described it as one
 * intent. One click, one point, and the arithmetic stays in `points.ts`.
 */
export const parseSpendSkillPoint = (message) => {
    if (!isRecord(message) || !hasExactly(message, ['skill', 'sequence']))
        return null;
    const { skill, sequence } = message;
    if (!isWireId(skill))
        return null;
    if (!isIntentSequence(sequence))
        return null;
    return { skill, sequence };
};
/**
 * The descent a message describes, or `null`.
 *
 * A sequence and **nothing else at all**, which is the entire check: this is the
 * one intent whose payload has no target, so the only thing a parser can do for
 * it is refuse the keys a client might be tempted to add. A `stairs` cell, a
 * `depth`, a `seed` — each of them would be a client deciding something the
 * server decides, and `hasExactly` makes all three unexpressible rather than
 * quietly ignored (`TODO.md` #441 is what ignoring them looks like).
 */
export const parseDescend = (message) => {
    if (!isRecord(message) || !hasExactly(message, ['sequence']))
        return null;
    const { sequence } = message;
    if (!isIntentSequence(sequence))
        return null;
    return { sequence };
};
//# sourceMappingURL=messages.js.map